import Vue from 'vue';
import { Data as ArticleData } from '@root/modules/article/types/article';
import { articleUrl } from '@root/common/filters/urlBuilders';
import { getArticleMeta, replaceArticleMeta } from '@root/views/article/utils/articleMeta';
import documentReferrer from '@root/common/utils/documentReferrer';

import getArticleAdsSettings from '@root/views/article/utils/getArticleAdsSettings';

interface Data {
  scrollDetected: boolean;
  removeListener: boolean;
  initScrollTop: number;
  scrollDirection: 'down' | 'up';
}

interface Methods {
  handleScroll: (event: Event) => void;
  updateHistory: () => void;
  setScrollDirection: () => void;
  isInViewport: (el: any) => boolean;
}

interface Props {
  article: ArticleData;
}

export default Vue.extend<Data, Methods, unknown, Props>({
  data() {
    return {
      scrollDetected: false,
      initScrollTop: 0,
      scrollDirection: 'down',
      removeListener: false,
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.initScrollTop = window.pageYOffset;
      window.addEventListener('scroll', this.handleScroll);
    });
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },
  methods: {
    updateHistory() {
      if (!this.article) {
        return false;
      }

      const queryString = Object.keys(this.$route.query)
        .map((key) => `${key}=${this.$route.query[key]}`)
        .join('&');

      const fakeDomainEnabled = this.$config.FAKE_DOMAIN_ENABLED;
      const articleDomain = this.$channelConfig('settings').domain;
      const articlePath = `${articleUrl({ article: this.article, domain: articleDomain, fakeDomainEnabled })}${queryString ? `?${queryString}` : ''}`;
      if (window.location.pathname !== articlePath) {
        window.history.replaceState({}, this.$options.filters!.stripTags(this.article.content.title.html), articlePath);
        const meta = getArticleMeta({ $channelConfig: this.$channelConfig, article: this.article, path: document.location.pathname });
        replaceArticleMeta({
          title: meta.title,
          meta: meta.data,
          style: meta.style,
          link: meta.link,
          __dangerouslyDisableSanitizers: ['style'],
        });
        const prevArticlePath = this.$store.getters['article/getActiveArticlePath'];
        const host = this.$channelConfig('meta').base.baseUrl;
        documentReferrer(`${host}${prevArticlePath}`);

        this.$store.commit('article/setActiveArticle', this.article);
        this.$store.commit('article/setActiveArticlePath', articlePath);
        this.$store.commit(
          'adForm/setPageAdsSettings',
          getArticleAdsSettings({ $channelConfig: this.$channelConfig, $route: this.$route, article: this.article })
        );

        // Dispatch custom event for player.js
        window.dispatchEvent(new CustomEvent('nuxtRouteChange'));

        window.document.documentElement.style.setProperty('--delfi-topbar-height', `0px`);
      }
    },
    setScrollDirection() {
      const scroll = window.pageYOffset;
      this.scrollDirection = scroll > this.initScrollTop ? 'down' : 'up';
      this.$store.commit('article/setScrollDirection', this.scrollDirection);
      this.scrollDetected = scroll !== this.initScrollTop;
      this.initScrollTop = scroll;
    },
    isInViewport(el) {
      const clientRect = el.getBoundingClientRect();
      return clientRect.top >= 0 || clientRect.bottom >= 0;
    },
    handleScroll() {
      const el = this.$refs.article as HTMLElement;

      if (!(window && this.isInViewport(el))) {
        return false;
      }

      const { clientHeight } = document.documentElement;
      const articleRect = el.getBoundingClientRect();
      this.setScrollDirection();

      if (!this.scrollDetected) {
        return;
      }

      if (this.scrollDirection === 'down') {
        // User scrolls down
        // User has reached next article title (article is at the bottom of the viewport)) - update url
        if (clientHeight - articleRect.top > Math.round(window.innerHeight * 0.3) && articleRect.bottom > clientHeight) {
          this.updateHistory();
        }
      }

      if (this.scrollDirection === 'up') {
        // User scrolls up
        // User has scrolled over current article title (article still in the viewport) - update url
        const articleSizeCheck = articleRect.height < window.innerHeight ? window.innerHeight / 2 : 0;
        if (articleRect.top - articleSizeCheck < 0 && articleRect.bottom > window.innerHeight) {
          this.updateHistory();
        }
      }
      return this.removeListener;
    },
  },
});
